import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ColorPicker} from "primereact/colorpicker";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PortalDialogDefault from "../../parameter/PortalDialogDefault";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import DocumentSettings from "../../../document/DocumentSettings";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Blacken,
	Functions,
	Information,
	PagePages,
	Rectangle,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxRedactRedact
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {InputNumber} from "primereact/inputnumber";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import RedactDialogFields from "../../field/redact/RedactDialogFields";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {PortalRedactAnnotation} from "../../../../shared/models/PortalRedactAnnotation";
import {InputSwitch} from "primereact/inputswitch";
import {TabPanel, TabView} from "primereact/tabview";
import Info from "../../../../shared/components/Info";
import {Slider} from "primereact/slider";
import {PortalToolboxAnnotationAnnotation} from "../../../../shared/models/PortalToolboxAnnotationAnnotation";
import MessageBox from "../../../../shared/components/MessageBox";

interface RedactPageDialogStatus extends PortalDialogState<PortalToolboxAnnotationAnnotation> {
	pages: number;
	currentPage: string;
	applyParameter?: Parameter<ToolboxRedactRedact>;
	panel: number;
}

export class RedactPageDialogDefinition extends PortalDialog<PortalToolboxAnnotationAnnotation,
	PortalDialogProperties<PortalToolboxAnnotationAnnotation>,
	RedactPageDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<RedactDialogFields, string>(),
			applyParameter: DialogDefault.getParameter(
				PortalDialogType.REDACT, this.props.context.documentManager.getSelection()
			),
			panel: 0
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.redact, Functions.getRedactDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.REDACT_PAGE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private renderGeneralTab(parameter: PortalToolboxAnnotationAnnotation): ReactNode {
		let errors: Map<RedactDialogFields, string> = this.state.fieldErrors;

		let annotationParameter: PortalRedactAnnotation = parameter!.add!.redact![0];
		if (!FunctionHelper.exists(annotationParameter?.position?.pathElement)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.CREATOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-creator">
							<Translate {...this.props} data="redact.content.creator.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-creator"
									   className={errors.has(RedactDialogFields.CREATOR) ? "p-invalid" : ""}
									   tooltip={errors.has(RedactDialogFields.CREATOR) ? Translate.value(
										   errors.get(RedactDialogFields.CREATOR)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   annotationParameter?.creator, PortalRedactAnnotation.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   RedactDialogFields.CREATOR, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(RedactDialogFields.CREATOR) ?
								Translate.value(
									errors.get(RedactDialogFields.CREATOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.SUBJECT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-subject">
							<Translate {...this.props} data="redact.content.subject.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   annotationParameter?.subject, PortalRedactAnnotation.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   RedactDialogFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.CONTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-text">
							<Translate {...this.props} data="redact.content.text.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea id="control-text" rows={2}
										   value={FunctionHelper.applyValue(
											   annotationParameter?.contents, PortalRedactAnnotation.getContentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.CONTENTS, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.INTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-intents">
							<Translate {...this.props} data="redact.content.intents" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-intents"
									   value={FunctionHelper.applyValue(
										   annotationParameter?.intents, PortalRedactAnnotation.getIntentsDefault()
									   )}
									   onInput={(e) => this.onChange(
										   RedactDialogFields.INTENTS, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.BORDER)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color-outline">
							<Translate {...this.props} data="redact.appearance.color.outline" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(RedactDialogFields.BORDER) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(RedactDialogFields.BORDER) ? "p-invalid" : ""}
										   value={FunctionHelper.applyValue(
											   annotationParameter?.color, PortalRedactAnnotation.getColorDefault()
										   )}
										   tooltip={errors.has(RedactDialogFields.BORDER) ? Translate.value(
											   errors.get(RedactDialogFields.BORDER)!, {ns: "message"}
										   ) : ""}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.BORDER, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color-outline" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 PortalRedactAnnotation.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 annotationParameter?.color, PortalRedactAnnotation.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.BORDER, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(RedactDialogFields.BORDER) ?
								Translate.value(
									errors.get(RedactDialogFields.BORDER)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.FILL)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color-interior">
							<Translate {...this.props} data="redact.appearance.color.interior" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(RedactDialogFields.FILL) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(RedactDialogFields.FILL) ? "p-invalid" : ""}
										   value={FunctionHelper.applyValue(
											   annotationParameter?.interiorColor,
											   PortalRedactAnnotation.getInteriorColorDefault()
										   )}
										   tooltip={errors.has(RedactDialogFields.FILL) ? Translate.value(
											   errors.get(RedactDialogFields.FILL)!, {ns: "message"}
										   ) : ""}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.FILL, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color-interior" className="p-inputgroup-addon"
											 format="hex"
											 defaultColor={
												 PortalRedactAnnotation.getInteriorColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 annotationParameter?.interiorColor,
												 PortalRedactAnnotation.getInteriorColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.FILL, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(RedactDialogFields.FILL) ?
								Translate.value(
									errors.get(RedactDialogFields.FILL)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(RedactDialogFields.APPLY)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-apply"></label>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-apply"
										 checked={FunctionHelper.applyValue(
											 annotationParameter.apply, PortalRedactAnnotation.getApplyDefault()
										 )}
										 onChange={(e) => this.onChange(
											 RedactDialogFields.APPLY, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-apply">
								<Translate {...this.props} data="redact.apply.label" namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(RedactDialogFields.PAGE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-page">
							<Translate {...this.props} data="redact.page.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className="p-inputgroup">
								<InputNumber inputId="control-page" showButtons={true} step={1} useGrouping={false}
											 value={FunctionHelper.applyValue(
												 annotationParameter?.page, PortalRedactAnnotation.getPageDefault()
											 )}
											 min={PortalRedactAnnotation.getPageMin()} max={this.state.pages}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.PAGE, e.value
											 )}/>
								<span className="p-inputgroup-addon">
									{Translate.value("inputs.pagerange.of", {pages: this.state.pages})}
								</span>
							</div>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderAdvancedTab(applyParameter: ToolboxRedactRedact): ReactNode {
		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid"
					 hidden={!this.state.fields?.contains(RedactDialogFields.COLLISION)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-collision">
						<Translate {...this.props} data="redact.apply.properties.collision.label"
								   namespace="dialog"/>
						<Info {...this.props} data="redact.apply.properties.collision.description"
							  options={{ns: "dialog"}}/>
					</label>
					<div className="p-col-12 p-sm-8 slider-input">
						<InputNumber inputId="control-collision" step={1} suffix=" %" showButtons={true}
									 useGrouping={false}
									 value={FunctionHelper.applyValue(
										 applyParameter?.blacken?.collisionRatio, Blacken.getCollisionRatioDefault()
									 )}
									 min={Blacken.getCollisionRatioMin()} max={Blacken.getCollisionRatioMax()}
									 onValueChange={(e) => this.onChange(
										 RedactDialogFields.COLLISION, e.value
									 )}/>
						<Slider value={FunctionHelper.applyValue(
							applyParameter?.blacken?.collisionRatio, Blacken.getCollisionRatioDefault()
						)}
								min={Blacken.getCollisionRatioMin()} max={Blacken.getCollisionRatioMax()} step={1}
								onChange={(e) => this.onChange(
									RedactDialogFields.COLLISION, e.value as number
								)}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!this.state.fields?.contains(RedactDialogFields.ANNOTATIONS)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-annotations"/>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-annotations"
									 checked={FunctionHelper.applyValue(
										 applyParameter?.blacken?.redactAnnotations,
										 Blacken.getRedactAnnotationsDefault()
									 )}
									 onChange={(e) => this.onChange(
										 RedactDialogFields.ANNOTATIONS, e.value
									 )}/>
						<label className="switch-label" htmlFor="control-annotations">
							<Translate {...this.props} data="redact.apply.properties.annotations.label"
									   namespace="dialog"/>
							<Info {...this.props} data="redact.apply.properties.annotations.description"
								  options={{ns: "dialog"}}/>
						</label>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!this.state.fields?.contains(RedactDialogFields.IMAGES)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-images"/>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-images"
									 checked={FunctionHelper.applyValue(
										 applyParameter?.blacken?.redactImages, Blacken.getRedactImagesDefault()
									 )}
									 onChange={(e) => this.onChange(
										 RedactDialogFields.IMAGES, e.value
									 )}/>
						<label className="switch-label" htmlFor="control-images">
							<Translate {...this.props} data="redact.apply.properties.images.label"
									   namespace="dialog"/>
							<Info {...this.props} data="redact.apply.properties.images.description"
								  options={{ns: "dialog"}}/>
						</label>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!this.state.fields?.contains(RedactDialogFields.PATHES)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-pathes"/>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-pathes"
									 checked={FunctionHelper.applyValue(
										 applyParameter?.blacken?.redactPaths, Blacken.getRedactPathsDefault()
									 )}
									 onChange={(e) => this.onChange(
										 RedactDialogFields.PATHES, e.value
									 )}/>
						<label className="switch-label" htmlFor="control-pathes">
							<Translate {...this.props} data="redact.apply.properties.pathes.label"
									   namespace="dialog"/>
							<Info {...this.props} data="redact.apply.properties.pathes.description"
								  options={{ns: "dialog"}}/>
						</label>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!this.state.fields?.contains(RedactDialogFields.TEXTS)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-texts"/>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-texts"
									 checked={FunctionHelper.applyValue(
										 applyParameter?.blacken?.redactTexts, Blacken.getRedactTextsDefault()
									 )}
									 onChange={(e) => this.onChange(
										 RedactDialogFields.TEXTS, e.value
									 )}/>
						<label className="switch-label" htmlFor="control-texts">
							<Translate {...this.props} data="redact.apply.properties.texts.label"
									   namespace="dialog"/>
							<Info {...this.props} data="redact.apply.properties.texts.description"
								  options={{ns: "dialog"}}/>
						</label>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let applyParameter: ToolboxRedactRedact | undefined = this.state.applyParameter?.data;
		if (!FunctionHelper.exists(parameter?.add?.redact) || !FunctionHelper.exists(applyParameter)) {
			return null;
		}

		let annotationParameter: PortalRedactAnnotation = parameter!.add!.redact![0];
		if (!FunctionHelper.exists(annotationParameter?.position?.pathElement)) {
			return null;
		}

		if (annotationParameter.apply) {
			return (
				<>
					<div className="p-col-12">
						<MessageBox type={MessageType.INFO} language={this.props.language} closeable={true}>
							<Translate {...this.props} data="redact.apply.info" namespace="dialog"/>
						</MessageBox>
					</div>
					<div className="p-col-12">
						<TabView activeIndex={this.state.panel} onTabChange={
							(e) => this.setState({panel: e.index})
						}>
							<TabPanel header={Translate.value("redact.properties", {ns: "dialog"})}
									  headerClassName={"control-tab-general"} contentClassName="general-tab-panel">
								{this.renderGeneralTab(parameter!)}
							</TabPanel>
							<TabPanel header={Translate.value("redact.advanced", {ns: "dialog"})}
									  headerClassName={"control-tab-advanced"} contentClassName="advanced-tab-panel">
								{this.renderAdvancedTab(applyParameter!)}
							</TabPanel>
						</TabView>
					</div>
				</>
			)
		} else {
			return this.renderGeneralTab(parameter!);
		}
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["redact-page-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.redact.page.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.redact.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: RedactDialogFields, value: any): void {
		let parameter: PortalToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: PortalRedactAnnotation = parameter.add!.redact![0];
		let applyParameter: ToolboxRedactRedact = this.state.applyParameter!.data;
		let errors: Map<RedactDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case RedactDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case RedactDialogFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case RedactDialogFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case RedactDialogFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case RedactDialogFields.FILL:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.interiorColor = value;
				break;
			case RedactDialogFields.BORDER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case RedactDialogFields.PAGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalRedactAnnotation.getPageDefault();
				}

				annotationParameter.page = value;
				break;
			case RedactDialogFields.APPLY:
				annotationParameter.apply = value;
				break;
			case RedactDialogFields.COLLISION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Blacken.getCollisionRatioDefault();
				}

				applyParameter.blacken!.collisionRatio = value;
				break;
			case RedactDialogFields.ANNOTATIONS:
				applyParameter.blacken!.redactAnnotations = value;
				break;
			case RedactDialogFields.IMAGES:
				applyParameter.blacken!.redactImages = value;
				break;
			case RedactDialogFields.PATHES:
				applyParameter.blacken!.redactPaths = value;
				break;
			case RedactDialogFields.TEXTS:
				applyParameter.blacken!.redactTexts = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter,
			applyParameter: this.state.applyParameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-redact-page"}/>
					<Translate {...this.props} data="redact.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let pageCount: number = await portalDocument.getNumberOfPages();
		let currentPage: number = documentSettings.activeViewer.currentPage;

		if (!FunctionHelper.exists(parameter?.add?.redact)) {
			return;
		}

		let annotationParameter: PortalRedactAnnotation | undefined = parameter!.add!.redact![0];
		annotationParameter.page = currentPage;

		if (annotationParameter.creator === "") {
			annotationParameter.creator = Translate.value("redact.content.creator.default", {ns: "dialog"});
		}

		if (annotationParameter.subject === "") {
			annotationParameter.subject = Translate.value("redact.content.subject.default", {ns: "dialog"});
		}

		if (annotationParameter.contents === "") {
			annotationParameter.contents = Translate.value("redact.content.text.default", {ns: "dialog"});
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage.toString(),
			applyParameter: DialogDefault.getParameter(
				PortalDialogType.REDACT, this.props.context.documentManager.getSelection()
			)
		});
	};

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter?.add?.redact)) {
			return;
		}

		let annotationParameter: PortalRedactAnnotation | undefined = parameter!.add!.redact![0];
		let rectangleParameter: Rectangle = annotationParameter.position!.pathElement![0];
		let pageMetadata: PagePages = portalDocument.getPage(annotationParameter.page!)!;
		let pageWidth: number = FunctionHelper.applyValue(pageMetadata.width, PagePages.getHeightDefault);
		let pageHeight: number = FunctionHelper.applyValue(pageMetadata.height, PagePages.getHeightDefault);
		rectangleParameter.width = UnitHelper.unitToUnit(
			pageMetadata.rotation === 0 || pageMetadata.rotation === 180 ? pageWidth : pageHeight,
			LengthUnit.POINTS, LengthUnit.MILLIMETER, this.props.portalResolution
		);
		rectangleParameter.height = UnitHelper.unitToUnit(
			pageMetadata.rotation === 0 || pageMetadata.rotation === 180 ? pageHeight : pageWidth,
			LengthUnit.POINTS, LengthUnit.MILLIMETER, this.props.portalResolution
		);

		try {
			this.setState({
				parameter: this.state.parameter
			}, async () => {
				await super.submit();

				let selection: Array<PortalDocument> = documentManager.getSelection();
				let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
				let operationData: Array<BaseToolbox> = [
					BaseToolbox.fromJson({
						annotation: this.state.parameter!.data.toJson()
					})
				];

				if (parameter!.add?.redact[0].apply) {
					PortalDialogDefault.setParameter(PortalDialogType.REDACT, this.state.applyParameter!, selection);

					operationData.push(
						BaseToolbox.fromJson({
							redact: this.state.applyParameter!.data.toJson()
						})
					);
				}

				for (let portalDocument of selection) {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});

					let message: Message = new Message("success.toolbox.redact.add", MessageType.SUCCESS);
					message.context = {filename: '"' + portalDocument.getFileName() + '" '};
					this.props.context.getPage().addMessage(message);
					this.hide();
				}
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.REDACT_PAGE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const RedactPageDialog = React.memo(RedactPageDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RedactPageDialog;